import { useMemo } from 'react';
import { PAGE_TO_MODULE, ALWAYS_ON_PAGES } from '@/lib/moduleMapping';

/**
 * Filters the Sidebar `navigation` tree according to a tenant's
 * `theme.modules` whitelist. Contract:
 *
 *   - modules == null  → legacy / not personalized → return navigation as-is
 *   - modules == []    → minimum (only always-on pages survive)
 *   - modules == [...] → only those + always-on pages
 *
 * Two-level filter: a group is hidden when every one of its subItems is
 * hidden. Contextual shortcuts can declare `moduleKey` to follow their area
 * even when the destination page is always-on elsewhere. Pages without a
 * `pageName` mapping in moduleMapping are kept
 * visible to avoid silent disappearance — the moduleMapping test enforces
 * coverage so this branch only matters for label-type entries.
 *
 * Pure (useMemo only). Reusable from any Sidebar implementation — the new
 * shell in PR4 will consume the same hook.
 */
export function useFilteredNavigation(navigation, modules) {
  return useMemo(() => filterNavigation(navigation, modules), [navigation, modules]);
}

/**
 * Whitelist de módulos que alimenta el filtro del Sidebar (Fase 3 — acceso
 * universal). La personalización del menú es BLANDA y por-empresa: se resuelve
 * exclusivamente desde `theme.modules`. El plan ya NO gobierna el menú (solo
 * cuotas/billing), así que `entitled_modules` deja de intervenir aquí.
 *
 *   - `themeModules == null/undefined` → sin personalizar → mostrar todo.
 *   - `themeModules == [...]`          → solo esos + always-on (ver ALWAYS_ON_PAGES).
 *
 * Personalizar el menú NUNCA bloquea el acceso: toda página sigue alcanzable
 * por URL (el gate duro por plan se retiró en Fase 3).
 *
 * Pura y sin dependencias de React — testeable en aislamiento.
 */
export function resolveNavModules(themeModules) {
  return themeModules ?? null;
}

export function filterNavigation(navigation, modules) {
  // Legacy fallback — null / undefined means "show everything".
  if (modules === null || modules === undefined) return navigation;

  const allowed = new Set(modules);
  const isAllowed = ({ pageName, moduleKey: shortcutModule }) => {
    if (shortcutModule) return allowed.has(shortcutModule);
    if (!pageName) return true; // labels and structural items pass through
    if (ALWAYS_ON_PAGES.has(pageName)) return true;
    const moduleKey = PAGE_TO_MODULE[pageName];
    if (!moduleKey) return true; // unmapped → keep visible (test enforces coverage)
    return allowed.has(moduleKey);
  };

  return navigation.flatMap((item) => {
    if (item.subItems) {
      const filteredSubs = item.subItems.filter((sub) => {
        if (sub.type === 'label') return true;
        return isAllowed(sub);
      });
      // Drop label-only or fully-empty groups.
      const hasReal = filteredSubs.some((s) => s.type !== 'label');
      if (!hasReal) return [];
      return [{ ...item, subItems: filteredSubs }];
    }
    return isAllowed(item) ? [item] : [];
  });
}
